import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { getBenchmark, getBenchmarkGrid, getBenchmarkMcaBasis, getMeta } from "../lib/api";
import { EvidencePanel } from "../components/EvidencePanel";
import { ResultNumbers } from "../components/ResultNumbers";
import { ErrorBox, Loading, PageHeader } from "../components/Shell";
import { Drawer, EvidenceButton } from "../components/Drawer";
import { CaveatNote, EvidenceSection, PlaceChip, SummaryBanner } from "../components/Evidence";
import { Select } from "./PartnersPage";

const BASES = [
  { value: "mgrant", label: "mGrant basis · one year, FY-2022 onward" },
  { value: "mca", label: "MCA basis · all ten filed years" },
];

export default function BenchmarkPage() {
  const [meta, setMeta] = useState(null);
  const [basis, setBasis] = useState("mgrant");
  const [theme, setTheme] = useState("health");
  const [fy, setFy] = useState("FY-2023");
  const [state, setState] = useState("");
  const [data, setData] = useState(null);
  const [evidence, setEvidence] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    getMeta().then(setMeta).catch(setErr);
  }, []);

  useEffect(() => {
    setData(null);
    setErr(null);
    const call =
      basis === "mca"
        ? getBenchmarkMcaBasis({ theme, state: state || undefined })
        : state
          ? getBenchmark({ state, theme, fy })
          : getBenchmarkGrid({ theme, fy, limit: 40 });
    call.then(setData).catch(setErr);
  }, [basis, state, theme, fy]);

  const entities = data?.bajaj_group_entities || meta?.bajaj_finserv_mca_entities || [];

  return (
    <div>
      <PageHeader title="Who else funds this, at what scale"
                  testId="benchmark-header"
                  actions={data && <EvidenceButton onClick={() => setEvidence(true)}
                                                   answerability={data.answerability} />} />

      <div className="flex gap-2 flex-wrap mb-4" data-testid="benchmark-filters">
        <Select value={basis} onChange={setBasis} testId="filter-basis" options={BASES} />
        <Select value={theme} onChange={setTheme} testId="filter-theme"
                options={(meta?.themes || []).map((t) => ({ value: t.key, label: t.label }))} />
        {basis === "mgrant" && (
          <Select value={fy} onChange={setFy} testId="filter-fy"
                  options={(meta?.mca_fys || []).map((f) => ({
                    value: f,
                    label: meta?.peer_comparable_fys?.includes(f)
                      ? `${f} — peers + Bajaj Finserv`
                      : `${f} — peers only (before mGrant)`,
                  }))} />
        )}
        <Select value={state} onChange={setState} label="All states" testId="filter-state"
                options={(meta?.peer_states || []).map((s) => ({ value: s, label: s }))} />
        {state && (
          <button onClick={() => setState("")} data-testid="clear-state-btn"
                  className="inline-flex items-center gap-1.5 text-[13px] px-3 py-2 rounded-full
                             border border-stone-300 text-stone-700 hover:bg-stone-50 transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> All states
          </button>
        )}
      </div>

      <p className="text-[13px] text-stone-600 mb-6 max-w-3xl leading-relaxed"
         data-testid="fy-overlap-note">
        {basis === "mca" ? (
          <>Filed spend against peer filings in the same {state ? `${state} ` : ""}cell, FY-2014 to FY-2023.</>
        ) : meta?.peer_comparable_fys?.includes(fy) ? (
          `${fy} exists in both datasets, so Bajaj Finserv's share of each peer cell is computed from mGrant.`
        ) : (
          `${fy} predates mGrant, so peer spend is shown and the Bajaj Finserv share is withheld. Switch to the MCA basis for its own filing.`
        )}
      </p>

      {!state && basis === "mgrant" && (
        <p className="text-[12px] text-stone-600 mb-3" data-testid="grid-hint">
          Click any state row to open that cell.
        </p>
      )}

      {err && <ErrorBox error={err} />}
      {!data && !err && <Loading />}

      {data && <ResultNumbers result={data} onRowClick={(r) => r.state_name && setState(r.state_name)} />}

      <Drawer open={evidence} onClose={() => setEvidence(false)} testId="benchmark-evidence-drawer">
        {data && (
          <EvidencePanel result={data} testId="benchmark-evidence">
            <div className="space-y-6">
              <SummaryBanner>
                {basis === "mca"
                  ? `Ten-year MCA basis for ${data.theme_label || theme}${state ? ` in ${state}` : " across India"} — Bajaj Finserv's own filings against peer filings, FY-2014 to FY-2023.`
                  : state
                    ? `Single cell: ${state} × ${theme} × ${fy}, with the Bajaj Finserv side taken from mGrant.`
                    : `Crowding grid across states for ${theme} × ${fy}. Click a state to open one cell.`}
              </SummaryBanner>

              <EvidenceSection label="Bajaj Finserv group entities in the MCA filings"
                               note={`${entities.length} entities`}>
                <div className="flex flex-wrap gap-1.5" data-testid="group-entities">
                  {entities.map((e) => <PlaceChip key={e}>{e}</PlaceChip>)}
                </div>
              </EvidenceSection>

              <CaveatNote title="What this is built on">
                628,701 filed project rows across 69,864 companies in the MCA CSR master. Cells with
                fewer than five filing companies are withheld rather than estimated. Two bases are
                offered because mGrant only starts FY-2022: the MCA basis puts Bajaj Finserv's own
                filings against peer filings for all ten years.
              </CaveatNote>
              <CaveatNote title="What peer spend is and is not">
                Peer spend is what companies filed with the MCA. It is not a like-for-like programme
                comparison: those filings carry no beneficiary counts, no districts, no
                implementation mode and no outlay, so none of those are shown. Filed spend and
                mGrant committed value are different measures and are never added together.
              </CaveatNote>
            </div>
          </EvidencePanel>
        )}
      </Drawer>
    </div>
  );
}
